import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';
import { ApiError } from '../../../../abstract/api-error';
import { SponsorRepresentativeService } from '../../../../core/data-services/sponsor-representative.service';
import { SponsorRepresentative } from '../../../../core/data-models/sponsor-representative.model';
import { SponsorRepresentativeCreateRequest } from '../../../../core/request-models/sponsor-representative-create-request.model';
import { SponsorRepresentativeUpdateRequest } from '../../../../core/request-models/sponsor-representative-update-request.model';

/**
 * Sponsor Representatives Component
 *
 * Standalone child component for managing sponsor representatives.
 * Displays a table with inline add/edit forms following the
 * point rates CRUD pattern.
 */
@Component({
  selector: 'app-sponsor-representatives',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxMaskDirective, PhoneFormatPipe],
  templateUrl: './sponsor-representatives.component.html',
  styleUrl: './sponsor-representatives.component.scss'
})
export class SponsorRepresentativesComponent implements OnInit {
  @Input() sponsorId = '';
  @Input() contactType = '';

  representatives: SponsorRepresentative[] = [];
  loading = true;
  errorMessage = '';
  successMessage = '';

  // Add form state
  showAddForm = false;
  addingRep = false;
  addFormError = '';
  newRep: SponsorRepresentativeCreateRequest = {
    name: '',
    title: null,
    territory: null,
    phone: null,
    email: null
  };

  // Edit state
  editingRepId: string | null = null;
  editData: SponsorRepresentativeUpdateRequest = {};
  editError = '';
  savingEdit = false;

  constructor(private sponsorRepresentativeService: SponsorRepresentativeService) {}

  ngOnInit(): void {
    if (this.sponsorId) {
      this.loadRepresentatives();
    }
  }

  /**
   * Whether the contact type excludes representatives
   */
  get isDisabled(): boolean {
    return this.contactType === 'locations_only' || this.contactType === 'contact_form_only';
  }

  /**
   * Informational message when representatives are not applicable
   */
  get infoMessage(): string {
    if (this.contactType === 'locations_only') {
      return 'This sponsor uses physical locations only. Change the contact type on the Info tab to manage representatives.';
    }
    if (this.contactType === 'contact_form_only') {
      return 'This sponsor uses a contact form only. Change the contact type on the Info tab to manage representatives.';
    }
    return '';
  }

  loadRepresentatives(): void {
    this.loading = true;
    this.errorMessage = '';

    this.sponsorRepresentativeService.listRepresentatives(this.sponsorId).subscribe({
      next: (reps) => {
        this.representatives = reps;
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load representatives:', err);
        this.errorMessage = 'Failed to load representatives';
        this.loading = false;
      }
    });
  }

  // ============================================
  // ADD REPRESENTATIVE
  // ============================================

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    this.addFormError = '';
    if (this.showAddForm) {
      this.resetNewRepForm();
    }
  }

  resetNewRepForm(): void {
    this.newRep = {
      name: '',
      title: null,
      territory: null,
      phone: null,
      email: null
    };
  }

  submitRepresentative(): void {
    this.addFormError = '';

    if (!this.newRep.name?.trim()) {
      this.addFormError = 'Representative name is required';
      return;
    }

    this.addingRep = true;

    const request: SponsorRepresentativeCreateRequest = {
      name: this.newRep.name.trim()
    };

    if (this.newRep.title?.trim()) {
      request.title = this.newRep.title.trim();
    }
    if (this.newRep.territory?.trim()) {
      request.territory = this.newRep.territory.trim();
    }
    if (this.newRep.phone?.trim()) {
      request.phone = this.newRep.phone.trim();
    }
    if (this.newRep.email?.trim()) {
      request.email = this.newRep.email.trim();
    }

    this.sponsorRepresentativeService.createRepresentative(this.sponsorId, request).subscribe({
      next: () => {
        this.showAddForm = false;
        this.addingRep = false;
        this.showSuccess('Representative created successfully');
        this.loadRepresentatives();
      },
      error: (err) => {
        console.error('Failed to create representative:', err);
        this.addFormError = ApiError.fromHttpError(err).message;
        this.addingRep = false;
      }
    });
  }

  // ============================================
  // EDIT REPRESENTATIVE
  // ============================================

  startEdit(rep: SponsorRepresentative): void {
    this.editingRepId = rep.id;
    this.editError = '';
    this.editData = {
      name: rep.name ?? undefined,
      title: rep.title,
      territory: rep.territory,
      phone: toNationalDigits(rep.phone),
      email: rep.email
    };
  }

  cancelEdit(): void {
    this.editingRepId = null;
    this.editData = {};
    this.editError = '';
  }

  saveEdit(): void {
    if (!this.editingRepId) return;

    this.editError = '';

    if (this.editData.name !== undefined && !this.editData.name?.trim()) {
      this.editError = 'Representative name cannot be empty';
      return;
    }

    this.savingEdit = true;

    this.sponsorRepresentativeService.updateRepresentative(this.sponsorId, this.editingRepId, this.editData).subscribe({
      next: () => {
        this.editingRepId = null;
        this.editData = {};
        this.savingEdit = false;
        this.showSuccess('Representative updated successfully');
        this.loadRepresentatives();
      },
      error: (err) => {
        console.error('Failed to update representative:', err);
        this.editError = ApiError.fromHttpError(err).message;
        this.savingEdit = false;
      }
    });
  }

  // ============================================
  // ARCHIVE REPRESENTATIVE
  // ============================================

  archiveRepresentative(rep: SponsorRepresentative): void {
    if (!confirm(`Are you sure you want to archive "${rep.name}"?`)) {
      return;
    }

    this.sponsorRepresentativeService.archiveRepresentative(this.sponsorId, rep.id).subscribe({
      next: () => {
        this.showSuccess('Representative archived successfully');
        this.loadRepresentatives();
      },
      error: (err) => {
        console.error('Failed to archive representative:', err);
        this.errorMessage = 'Failed to archive representative';
      }
    });
  }

  // ============================================
  // UTILITY
  // ============================================

  showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 4000);
  }
}
